import { config } from '../config'
import { accessToken, currentClaims, endSession, logout } from './session'

// The signed-in account, over the pool's own API. docs/plan/account.md §4.
//
// **The only reader of the access token.** Everything else this client
// sends goes to our surfaces with the ID token; this goes to
// `cognito-idp.<region>.amazonaws.com` with the access token, because
// self-service enrolment, a password change and a global sign-out are
// operations the pool performs on the account holder's say-so and on
// nobody else's (§2.2). No server of ours is in the path.
//
// **No SDK** (§2.6). Every operation is one JSON `POST` naming itself in
// `X-Amz-Target`, and a client for a hundred operations taken for seven
// is a worse bargain than this file.
//
// **Errors are mapped once, here**, into the sentences the forms show.
// Nothing under `screens/` names an operation or a `__type`.

/** What `describe` answers: who is signed in, and whether a second factor is on. */
export interface AccountDescription {
  email: string
  emailVerified: boolean
  name?: string
  mfa: 'off' | 'totp'
}

/** What `beginTotp` answers: the secret, and the URI an authenticator scans. */
export interface TotpEnrolment {
  secret: string
  otpauthUri: string
}

export interface AccountPort {
  describe(): Promise<AccountDescription>
  changePassword(current: string, proposed: string): Promise<void>
  beginTotp(): Promise<TotpEnrolment>
  confirmTotp(code: string, deviceName?: string): Promise<void>
  disableTotp(): Promise<void>
  updateName(name: string): Promise<void>
  signOutEverywhere(): Promise<void>
}

/**
 * A refusal, in the words a form shows.
 *
 * `title` is set only where the sentence is the pool's own message rather
 * than one of ours — "Something went wrong", with what the pool said
 * under it verbatim, because a message nobody here anticipated is better
 * shown whole than paraphrased into something wrong.
 */
export class AccountError extends Error {
  readonly title?: string
  /** The pool's `__type`, for a caller that has to tell two refusals apart. */
  readonly type?: string

  constructor(message: string, options: { title?: string; type?: string } = {}) {
    super(message)
    this.name = 'AccountError'
    this.title = options.title
    this.type = options.type
  }
}

export const WRONG_PASSWORD = 'The current password is not right.'
export const CODE_MISMATCH = 'That code did not match. Codes change every 30 seconds — try the current one.'
export const TOO_MANY = 'Too many attempts. Wait a few minutes and try again.'
export const SESSION_OVER = 'Your session has ended. Sign in again.'
export const SOMETHING_WENT_WRONG = 'Something went wrong'

/** What an authenticator lists the account under, and who issued it. */
export const ISSUER = 'Bluu CMS'

/**
 * The URI an authenticator app scans, per the Key URI Format.
 *
 * The label is `issuer:account`, each half escaped and the colon between
 * them not — an authenticator splits on it — and the issuer is repeated
 * as a parameter, which is the one newer apps read.
 */
export function otpauthUri(account: string, secret: string): string {
  const issuer = encodeURIComponent(ISSUER)
  return (
    `otpauth://totp/${issuer}:${encodeURIComponent(account)}` +
    `?secret=${encodeURIComponent(secret)}&issuer=${issuer}`
  )
}

/** What the port is built over. Injected so tests can fake the pool. */
export interface AccountDeps {
  fetch: typeof fetch
  region: () => string | undefined
  accessToken: () => Promise<string | null>
  /** The signed-in address, for the authenticator's label. */
  email: () => string | undefined
  endSession: () => void
  logout: () => void
}

type Operation =
  | 'GetUser'
  | 'ChangePassword'
  | 'AssociateSoftwareToken'
  | 'VerifySoftwareToken'
  | 'SetUserMFAPreference'
  | 'UpdateUserAttributes'
  | 'DeleteUserAttributes'
  | 'GlobalSignOut'

interface GetUserResponse {
  Username: string
  UserAttributes?: { Name: string; Value?: string }[]
  UserMFASettingList?: string[]
  PreferredMfaSetting?: string
}

export function createAccount(deps: AccountDeps): AccountPort {
  async function call<T>(operation: Operation, body: Record<string, unknown>): Promise<T> {
    const region = deps.region()
    if (!region) throw new AccountError('This client has no pool to manage an account in.', { title: SOMETHING_WENT_WRONG })
    const token = await deps.accessToken()
    if (!token) {
      // Signed out, or a tab signed in before the access token was kept.
      // Either way the account cannot be reached without signing in, and
      // a sign-in is a redirect while the hosted session lasts.
      deps.endSession()
      throw new AccountError(SESSION_OVER, { type: 'NotAuthorizedException' })
    }

    let response: Response
    try {
      response = await deps.fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
        },
        body: JSON.stringify({ ...body, AccessToken: token }),
      })
    } catch {
      throw new AccountError('The account service could not be reached.', { title: SOMETHING_WENT_WRONG })
    }

    const parsed = await jsonOf(response)
    if (response.ok) return parsed as T
    throw refusal(operation, parsed, deps)
  }

  const setTotp = (enabled: boolean) =>
    call('SetUserMFAPreference', { SoftwareTokenMfaSettings: { Enabled: enabled, PreferredMfa: enabled } })

  return {
    async describe() {
      const user = await call<GetUserResponse>('GetUser', {})
      const attribute = (name: string) => user.UserAttributes?.find((candidate) => candidate.Name === name)?.Value
      // Whether TOTP is *on* is the setting list; the preference only
      // says which factor is asked for when several are, and there is one.
      return {
        email: attribute('email') ?? user.Username,
        emailVerified: attribute('email_verified') === 'true',
        name: attribute('name') || undefined,
        mfa: user.UserMFASettingList?.includes('SOFTWARE_TOKEN_MFA') ? 'totp' : 'off',
      }
    },

    async changePassword(current, proposed) {
      await call('ChangePassword', { PreviousPassword: current, ProposedPassword: proposed })
    },

    async beginTotp() {
      const { SecretCode } = await call<{ SecretCode: string }>('AssociateSoftwareToken', {})
      return { secret: SecretCode, otpauthUri: otpauthUri(deps.email() ?? '', SecretCode) }
    },

    async confirmTotp(code, deviceName) {
      const verified = await call<{ Status?: string }>('VerifySoftwareToken', {
        UserCode: code,
        ...(deviceName ? { FriendlyDeviceName: deviceName } : {}),
      })
      if (verified.Status === 'ERROR') throw new AccountError(CODE_MISMATCH, { type: 'CodeMismatchException' })
      // Two calls, because a verified token the pool does not *prefer* is
      // one it never asks for.
      await setTotp(true)
    },

    async disableTotp() {
      await setTotp(false)
    },

    async updateName(name) {
      const trimmed = name.trim()
      // An empty value is not a name the pool will store; clearing one is
      // deleting the attribute, which the same write permission allows.
      if (trimmed) await call('UpdateUserAttributes', { UserAttributes: [{ Name: 'name', Value: trimmed }] })
      else await call('DeleteUserAttributes', { UserAttributeNames: ['name'] })
    },

    async signOutEverywhere() {
      await call('GlobalSignOut', {})
      // The revocation ends every token, but not the hosted session
      // cookie on the pool's domain, which is not a token — so the
      // pool's logout as well, or the next sign-in is a silent redirect.
      deps.logout()
    },
  }
}

/** A body that is not JSON — a proxy's error page — reads as no body. */
async function jsonOf(response: Response): Promise<Record<string, unknown>> {
  try {
    const parsed: unknown = JSON.parse(await response.text())
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/** The pool's refusal, as the sentence a form shows. */
function refusal(operation: Operation, body: Record<string, unknown>, deps: AccountDeps): AccountError {
  // `__type` may arrive namespaced (`…#NotAuthorizedException`); the
  // name is the last part either way.
  const type = String(body.__type ?? '').split('#').pop() ?? ''
  const message = typeof body.message === 'string' ? body.message : typeof body.Message === 'string' ? body.Message : ''

  switch (type) {
    case 'NotAuthorizedException':
      if (operation === 'ChangePassword') return new AccountError(WRONG_PASSWORD, { type })
      // Anywhere else it is the token the pool refused: revoked, expired
      // or signed out everywhere from another device. The session is over.
      deps.endSession()
      return new AccountError(SESSION_OVER, { type })
    case 'InvalidPasswordException':
      // The pool's own words name the rule the password broke.
      return new AccountError(message || 'That password does not meet the policy.', { type })
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return new AccountError(CODE_MISMATCH, { type })
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return new AccountError(TOO_MANY, { type })
    default:
      return new AccountError(message || `The account service refused this (${type || 'no reason given'}).`, {
        title: SOMETHING_WENT_WRONG,
        type: type || undefined,
      })
  }
}

/** The port over the real pool and the real session. */
export const account: AccountPort = createAccount({
  fetch: (...args) => fetch(...args),
  region: () => config.cognito?.region,
  accessToken,
  email: () => currentClaims()?.email,
  endSession,
  logout,
})
